import { Component } from '@angular/core';
import { environment } from '../environments/environment';
import dexFreight, { Microservices } from '@microservices';
import { UserService } from './user.service';
import { CompanySettingsService } from './company-settings.service';
import { DocumentsService } from './documents.service';
import { EnumsService } from './enums.service';
import { MainService } from './services/main/main.service';
import { KeyEventsService } from './key-events.service';
import { DrayageService } from './services/back-services/drayage.service';
import { NotificationsService } from './services/notification/notifications.service';
import { ObservablesService } from './services/observables/observables.service';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { CompanyService } from './company.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastService } from 'src/modules/primitives/toasts/toast.service';
import { Router } from '@angular/router';
import { RoutesApp } from './constants/Routes.constants';
import { LoadLocationService } from './load-location.service';
import { IpcService } from './services/ipc.service';
import { IPCChannel } from './@enums/IPCChannel.enum';
import { AuthService } from '@services';
import { VehiclesService } from './vehicles.service';
import { TrailersService } from './trailers.service';
import { GpsService } from './gps.service';
import { DataRegistryService } from './data-registry.service';
import { DevModeService } from './dev-mode.service';
import { AuthViewportService } from '../modules/auth/auth-viewport.service';
import { Notification } from './@types/Notification.type';
import debug from "debug";

debug.enable('@*');

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  env = environment;
  bannerUrl = '/assets/banner-es.png';
  emailToLoadProcessing = false;

  listeners: Subscription[] = [];
  private emailToLoadProcessingIds = new Set<string>();
  private emailToLoadProcessingTimeout?: number;

  onlineEvent: Observable<Event> | undefined;
  offlineEvent: Observable<Event>;

  isMaintenance = false;

  constructor(
    private router: Router,
    private micro: Microservices,
    private snackbar: MatSnackBar,
    private toast: ToastService,
    private authService: AuthService,
    private userService: UserService,
    private companyService: CompanyService,
    private companySettingsService: CompanySettingsService,
    private documentsService: DocumentsService,
    private enumsService: EnumsService,
    private mainService: MainService,
    private keyEventsService: KeyEventsService,
    private drayageService: DrayageService,
    private notificationsService: NotificationsService,
    private observablesService: ObservablesService,
    private loadLocations: LoadLocationService,
    private vehiclesService: VehiclesService,
    private trailersService: TrailersService,
    private gpsService: GpsService,
    private ipc: IpcService,
    private ds: DataRegistryService,
    private authViewport: AuthViewportService,
    public devModeService: DevModeService
  ) {
    // Las pantallas de auth necesitan el viewport real del dispositivo; el resto
    // de la app se queda con el de layout por defecto. Ver AuthViewportService.
    this.authViewport.watch();
  }

  async ngOnInit() {
    // console.clear();

    this.ds.init();
    this.monitorNetworkStatus();
    this.getGlobalSettings();

    this.devModeService.setupListeners();
    this.micro.setupListeners();
    this.userService.setupListeners();
    this.companyService.setupListeners();
    this.companySettingsService.setupListeners();

    this.mainService.setupListeners();
    this.documentsService.setupListeners();
    this.enumsService.setupListeners();
    this.keyEventsService.setupListeners();
    this.drayageService.setupListeners();
    this.listenForEmailToLoadProcessing();
    this.notificationsService.setupListeners();
    this.observablesService.setupListeners();
    this.loadLocations.setupListeners();
    this.vehiclesService.setupListeners();
    this.trailersService.setupListeners();
    this.gpsService.setupListeners();

    this.listeners.push(
      this.ipc.onChannelMessage(IPCChannel.Logout).subscribe(() => {
        this.authService.clearSession();
      })
    );
  }

  ngOnDestroy(): void {
    this.listeners.forEach(listener => listener.unsubscribe());
    this.hideEmailToLoadProcessing();
    this.micro.removeListeners();
    this.mainService.removeListeners();
    this.userService.removeListeners();
    this.companySettingsService.removeListeners();
    this.documentsService.removeListeners();
    this.enumsService.removeListeners();
    this.keyEventsService.removeListeners();
    this.loadLocations.removeListeners();
    this.notificationsService.removeListeners();
    this.vehiclesService.removeListeners();
    this.trailersService.removeListeners();
    this.gpsService.removeListeners();
    this.devModeService.removeListeners();
    this.ds.cleanup();
  }

  monitorNetworkStatus() {
    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');

    this.listeners.push(this.onlineEvent.subscribe(_ => {
      this.snackbar.open('Conectado nuevamente', '', {
        verticalPosition: 'top',
        duration: 2000
      });
    }));

    this.listeners.push(this.offlineEvent.subscribe(_ => {
      this.snackbar.open('Reconectando...', '', {
        verticalPosition: 'top'
      });
    }));
  }

  async getGlobalSettings() {
    try {
      this.isMaintenance = await dexFreight.Status.getMaintenanceStatus();

      if (this.isMaintenance) {
        this.router.navigate([RoutesApp.Maintenance]);
      }
    } catch (error) {
      console.log(error);
    }
  }

  private listenForEmailToLoadProcessing() {
    this.listeners.push(
      this.notificationsService.getNotificationStream().subscribe((notification: Notification) => {
        if (notification.type === 'emailToLoadProcessingStarted') {
          this.showEmailToLoadProcessing(this.emailToLoadProcessingId(notification));
        }

        if (notification.type === 'emailToLoadDraftPublished') {
          this.hideEmailToLoadProcessing(this.emailToLoadProcessingId(notification));
        }

        if (notification.type === 'emailToLoadProcessingFailed') {
          this.hideEmailToLoadProcessing(this.emailToLoadProcessingId(notification));
          this.toast.error('No pudimos procesar tu correo', 'No se generó ningún borrador. Contáctanos si necesitas ayuda.');
        }
      })
    );
  }

  private emailToLoadProcessingId(notification: Notification) {
    return String(notification.data?.providerMessageId ?? 'email-to-load');
  }

  private showEmailToLoadProcessing(providerMessageId: string) {
    this.emailToLoadProcessingIds.add(providerMessageId);
    this.emailToLoadProcessing = true;

    if (this.emailToLoadProcessingTimeout) {
      window.clearTimeout(this.emailToLoadProcessingTimeout);
    }

    this.emailToLoadProcessingTimeout = window.setTimeout(() => {
      this.hideEmailToLoadProcessing();
    }, 90000);
  }

  private hideEmailToLoadProcessing(providerMessageId?: string) {
    if (providerMessageId) {
      this.emailToLoadProcessingIds.delete(providerMessageId);
    } else {
      this.emailToLoadProcessingIds.clear();
    }

    if (this.emailToLoadProcessingIds.size > 0) {
      this.emailToLoadProcessing = true;
      return;
    }

    this.emailToLoadProcessing = false;

    if (this.emailToLoadProcessingTimeout) {
      window.clearTimeout(this.emailToLoadProcessingTimeout);
      this.emailToLoadProcessingTimeout = undefined;
    }
  }

}
